import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ShoppingCart, Check, Truck, PackageCheck, Download, MessageSquare, Upload, Phone, Mail, Split, FileText, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  getPurchaseIndents, updatePurchaseIndent, receivePartial, shortCloseIndent, getFirms, adminRevertItem,
  getMySuppliers, getPurchaseCommunications, addPurchaseCommunication, setIndentDrawing,
  splitPurchaseIndent, setPurchaseItemAlias, getSalesOrder
} from '../../services/taskApi';
import { fileUrl } from '../../utils/fileUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Awaiting PO': 'bg-red-500/15 text-red-400',
  'PO Placed': 'bg-amber-500/15 text-amber-400',
  'In Transit': 'bg-blue-500/15 text-blue-400',
  'Partially Received': 'bg-purple-500/15 text-purple-400',
  'Received': 'bg-green-500/15 text-green-400',
};

// Friendly labels shown to users — captures the lifecycle in plain English:
// • Indent Initiated = SO transferred to Purchase, supplier PO not yet placed
// • PO Placed / In Transit = supplier has been engaged
// • Indent Completed = goods received back from supplier
const STATUS_LABELS = {
  'Awaiting PO': 'Indent Initiated',
  'PO Placed': 'PO Placed',
  'In Transit': 'In Transit',
  'Partially Received': 'Partially Received',
  'Received': 'Indent Completed',
};

const FlowPurchase = () => {
  const { isDark } = useOutletContext();
  const [indents, setIndents] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('initiated'); // 'initiated' | 'procurement' | 'completed'
  const [editing, setEditing] = useState({});

  // Map UI tab → backend status filter list. "In Procurement" spans 3 statuses, so we fetch unfiltered for that tab.
  const TAB_FILTERS = {
    initiated: ['Awaiting PO'],
    procurement: ['PO Placed', 'In Transit', 'Partially Received'],
    completed: ['Received'],
  };

  // Supplier autocomplete
  const [supplierSuggest, setSupplierSuggest] = useState([]);
  const [openSupplierFor, setOpenSupplierFor] = useState(null);

  // Communications log dialog
  const [commIndent, setCommIndent] = useState(null);
  const [commLog, setCommLog] = useState([]);
  const [commForm, setCommForm] = useState({ channel: 'Call', note: '', attachment_url: '' });

  // Split indent dialog (Feature #2 — trial-then-bulk procurement)
  const [splitIndent, setSplitIndent] = useState(null);
  const [splitForm, setSplitForm] = useState({ split_qty: 1, reason: 'Trial / partial procurement' });

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany) params.company_id = filterCompany;
      if (search) params.q = search;
      const [iRes, fRes] = await Promise.all([getPurchaseIndents(params), getFirms()]);
      setIndents(iRes.data || []);
      setFirms(fRes.data || []);
    } catch (err) { console.error('FlowPurchase: fetch failed', err); }
    finally { setLoading(false); }
  }, [filterCompany, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client-side counts and filter per tab
  const tabCounts = {
    initiated: indents.filter(i => TAB_FILTERS.initiated.includes(i.procurement_status)).length,
    procurement: indents.filter(i => TAB_FILTERS.procurement.includes(i.procurement_status)).length,
    completed: indents.filter(i => TAB_FILTERS.completed.includes(i.procurement_status)).length,
  };
  const [groupSO, setGroupSO] = useState(false);
  const [soPreview, setSoPreview] = useState(null); // { loading, order }
  const visibleIndents = React.useMemo(() => {
    const list = indents.filter(i => TAB_FILTERS[tab].includes(i.procurement_status));
    return groupSO ? [...list].sort((a, b) => (a.so_number || '').localeCompare(b.so_number || '')) : list;
  }, [indents, tab, groupSO]);

  const openSoPreview = async (ind) => {
    setSoPreview({ loading: true, order: null });
    try {
      const res = await getSalesOrder(ind.sales_order_id);
      setSoPreview({ loading: false, order: res.data });
    } catch (err) { toast.error('Failed to load SO'); setSoPreview(null); }
  };

  const handleUpdate = async (id) => {
    const edits = editing[id];
    if (!edits) return;
    try {
      await updatePurchaseIndent(id, edits);
      toast.success('Indent updated');
      setEditing(e => { const n = { ...e }; delete n[id]; return n; });
      // Update locally — NO refetch, so the user's scroll position & filters are preserved
      setIndents(prev => prev.map(i => (i.id === id ? { ...i, ...edits } : i)));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReceived = async (id, ind) => {
    const pending = (ind.quantity_short || 0) - (ind.quantity_received || 0);
    const input = window.prompt(`Enter received qty (1–${pending}). Goods will go to Store for cross-verification.`, String(pending));
    if (!input) return;
    const rcv = parseInt(input);
    if (!rcv || rcv < 1 || rcv > pending) { toast.error(`Qty must be 1–${pending}`); return; }
    try {
      const res = await receivePartial(id, { received_qty: rcv });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleShortClose = async (id, ind) => {
    const pending = (ind.quantity_short || 0) - (ind.quantity_received || 0);
    if (pending <= 0) { toast.error('Nothing left to short-close'); return; }
    const ok = window.confirm(
      `Short-close ${pending} unit(s) of "${ind.sku_or_item_name}"?\n\n` +
      `This cancels the unreceived portion of this indent. The supplier will NOT deliver these units. ` +
      `This action cannot be undone — proceed only if you're sure.`
    );
    if (!ok) return;
    const reason = window.prompt('Reason for short-close (required):', '');
    if (reason === null) return;
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    try {
      const res = await shortCloseIndent(id, reason.trim());
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const editField = (id, field, val) => {
    setEditing(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: val } }));
  };

  const saveAlias = async (ind) => {
    const alias = (editing[ind.id]?.purchase_item_name ?? '').trim();
    try {
      const r = await setPurchaseItemAlias(ind.so_item_id, alias);
      toast.success(r.data.message || 'Saved');
      setEditing(e => { const n = { ...(e[ind.id] || {}) }; delete n.purchase_item_name; return { ...e, [ind.id]: n }; });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const fetchSupplierSuggest = async (q) => {
    try { const res = await getMySuppliers(q || ''); setSupplierSuggest(res.data || []); }
    catch { setSupplierSuggest([]); }
  };

  const openComms = async (ind) => {
    setCommIndent(ind);
    setCommForm({ channel: 'Call', note: '', attachment_url: '' });
    try { const r = await getPurchaseCommunications(ind.id); setCommLog(r.data || []); }
    catch { setCommLog([]); }
  };

  const submitComm = async (e) => {
    e.preventDefault();
    if (!commForm.note.trim()) { toast.error('Note is required'); return; }
    try {
      await addPurchaseCommunication(commIndent.id, commForm);
      toast.success('Log added');
      const r = await getPurchaseCommunications(commIndent.id);
      setCommLog(r.data || []);
      setCommForm({ channel: 'Call', note: '', attachment_url: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleIndentDrawingUpload = async (indentId, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const token = localStorage.getItem('task_token');
    try {
      const r = await fetch(`${API_URL}/api/tasks-app/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const d = await r.json();
      if (d.url) {
        await setIndentDrawing(indentId, { drawing_url: d.url });
        toast.success('Drawing uploaded and synced to Sales Order');
        fetchData();
      }
    } catch { toast.error('Upload failed'); }
  };

  const openSplit = (ind) => {
    setSplitIndent(ind);
    setSplitForm({ split_qty: 1, reason: 'Trial / partial procurement' });
  };

  const submitSplit = async (e) => {
    e.preventDefault();
    const qty = parseInt(splitForm.split_qty);
    const total = splitIndent.quantity_short;
    if (!qty || qty < 1 || qty >= total) {
      toast.error(`Split qty must be 1–${total - 1} (cannot split off the whole indent)`); return;
    }
    try {
      const r = await splitPurchaseIndent(splitIndent.id, { split_qty: qty, reason: splitForm.reason });
      toast.success(r.data.message);
      setSplitIndent(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Split failed'); }
  };

  // Validation helpers for #7 — robust Purchase Indent UX
  const indentValidationIssues = (ind) => {
    const edits = editing[ind.id] || {};
    const target = (edits.procurement_status ?? ind.procurement_status);
    if (target === 'PO Placed' || target === 'In Transit') {
      const missing = [];
      if (!(edits.tally_po_no ?? ind.tally_po_no)) missing.push('PO #');
      if (!(edits.tally_po_date ?? ind.tally_po_date)) missing.push('PO Date');
      if (!(edits.supplier_name ?? ind.supplier_name)) missing.push('Supplier');
      if (!(edits.supplier_eta ?? ind.supplier_eta)) missing.push('ETA');
      return missing;
    }
    return [];
  };

  const handleUpdateWithConfirm = async (id, ind) => {
    const edits = editing[id] || {};
    const newStatus = edits.procurement_status;
    if (newStatus && newStatus !== ind.procurement_status && (newStatus === 'PO Placed' || newStatus === 'In Transit')) {
      const issues = indentValidationIssues(ind);
      if (issues.length > 0) {
        toast.error(`Cannot advance to ${newStatus} — missing: ${issues.join(', ')}`); return;
      }
      if (!window.confirm(`Confirm: advance indent to "${newStatus}"?\n\nSupplier: ${edits.supplier_name ?? ind.supplier_name}\nETA: ${edits.supplier_eta ?? ind.supplier_eta}\nPO #: ${edits.tally_po_no ?? ind.tally_po_no}`)) return;
    }
    handleUpdate(id);
  };

  const getFirmName = (id) => firms.find(f => f.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="flow-purchase">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Purchase Indents</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Track shortages & procurement</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Search SO #, item, our item name, supplier, PO #..."            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`h-9 w-56 text-xs ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : ''}`}
            data-testid="purchase-search-input"
          />
          <Button size="sm" variant={groupSO ? 'default' : 'outline'} onClick={() => setGroupSO(g => !g)}
            className={groupSO ? 'bg-blue-600 hover:bg-blue-700 h-9 text-xs' : `h-9 text-xs ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
            data-testid="purchase-group-so-toggle">
            SO Grouping {groupSO ? 'ON' : 'OFF'}
          </Button>
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      <Dialog open={!!soPreview} onOpenChange={(v) => { if (!v) setSoPreview(null); }}>
        <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="purchase-so-preview-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {soPreview?.order ? `${soPreview.order.so_number} — ${soPreview.order.customer_name}` : 'Sales Order'}
            </DialogTitle>
          </DialogHeader>
          {soPreview?.loading ? (
            <div className={`text-center py-8 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Loading…</div>
          ) : soPreview?.order && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div><span className="opacity-50">Agent</span><p className="font-medium">{soPreview.order.agent_name || '—'}</p></div>
                <div><span className="opacity-50">SO Date</span><p className="font-medium">{(soPreview.order.created_at || '').slice(0, 10)}</p></div>
                <div><span className="opacity-50">Customer PO</span><p className="font-medium">{soPreview.order.customer_po_number || '—'}</p></div>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {(soPreview.order.items || []).map(it => (
                    <tr key={it.id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className="py-1.5 pr-2">{it.item_name}</td>
                      <td className="py-1.5 pr-2">{it.quantity_ordered}</td>
                      <td className="py-1.5">{it.fulfillment_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tabs — mirrors the Store page layout so Purchase users get a consistent funnel */}
      <div className={`flex flex-col sm:flex-row gap-1 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {[
          { k: 'initiated', label: 'Indent Initiated', hint: 'Awaiting PO' },
          { k: 'procurement', label: 'In Procurement', hint: 'PO Placed → Partial Received' },
          { k: 'completed', label: 'Indent Completed', hint: 'Received' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`purchase-tab-${t.k}`}
            className={`flex-1 px-4 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2 ${
              tab === t.k
                ? isDark ? 'bg-white/10 text-white shadow' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'
            }`}
            title={t.hint}
          >
            <span>{t.label}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
              tab === t.k
                ? isDark ? 'bg-white/15 text-white' : 'bg-gray-200 text-gray-700'
                : isDark ? 'bg-white/5 text-white/50' : 'bg-gray-200/60 text-gray-500'
            }`}>{tabCounts[t.k]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`h-16 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : visibleIndents.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <ShoppingCart size={40} className={`mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>
            {tab === 'initiated' ? 'No new indents waiting for PO' : tab === 'procurement' ? 'No indents currently in procurement' : 'No completed indents yet'}
          </p>
        </CardContent></Card>
      ) : (
        <Card className={cardCls}>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO #</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Short Qty</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Tally PO # / Date</th>
                    <th className={`text-left py-2.5 px-2 font-medium uppercase tracking-wider min-w-[200px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Supplier</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>ETA *</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Status</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Files</th>
                    <th className={`text-center py-2.5 px-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleIndents.map((ind) => {
                    const edits = editing[ind.id] || {};
                    const isReceived = ind.procurement_status === 'Received';
                    return (
                      <tr key={ind.id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-50 hover:bg-gray-50'} transition-colors`}>
                        <td className={`py-3 px-2 font-medium`}>
                          <button onClick={() => openSoPreview(ind)} className={`underline-offset-2 hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`} title="View SO preview" data-testid={`purchase-so-preview-${ind.id}`}>
                            {ind.so_number}
                          </button>
                          {ind.so_date && <div className={`text-[9px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SO: {ind.so_date}</div>}
                          <div className={`text-[9px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{getFirmName(ind.company_id)}</div>
                        </td>
                        <td className={`py-3 px-2 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div>{ind.sku_or_item_name}</div>
                          {isReceived ? (
                            ind.purchase_item_name && <div className="text-[9px] text-purple-400">Our name: {ind.purchase_item_name}</div>
                          ) : (
                            <div className="mt-1 flex items-center gap-1">
                              <Input className={`h-6 text-[10px] w-44 ${inputCls}`} placeholder="Our item name (optional)"
                                value={edits.purchase_item_name ?? ind.purchase_item_name ?? ''}
                                onChange={e => editField(ind.id, 'purchase_item_name', e.target.value)}
                                data-testid={`purchase-alias-input-${ind.id}`} />
                              {edits.purchase_item_name !== undefined && edits.purchase_item_name !== (ind.purchase_item_name || '') && (
                                <button type="button" onClick={() => saveAlias(ind)} className="text-green-400 hover:text-green-300"
                                  title="Save item name" data-testid={`purchase-alias-save-${ind.id}`}>
                                  <Check size={13} />
                                </button>
                              )}
                            </div>
                          )}
                          {ind.customer_name && <div className={`text-[9px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Cust: {ind.customer_name}</div>}
                          {ind.admin_remarks && (
                            <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-amber-500 ${isDark ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-800'}`} title={`By ${ind.admin_remarks_by || '-'}`}>
                              💬 <span className="font-medium">Admin:</span> {ind.admin_remarks}
                            </div>
                          )}
                          {ind.store_remarks && (
                            <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-sky-500 ${isDark ? 'bg-sky-500/10 text-sky-200' : 'bg-sky-50 text-sky-800'}`} title={`By ${ind.store_remarks_by || '-'}`} data-testid={`store-remark-${ind.id}`}>
                              📦 <span className="font-medium">Store note:</span> {ind.store_remarks}
                            </div>
                          )}
                          {ind.split_parent_id && (
                            <div className={`mt-1 text-[9px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${isDark ? 'bg-purple-500/10 text-purple-300' : 'bg-purple-50 text-purple-700'}`} title="Split from another indent">
                              <Split size={9} /> split lot
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          <div className={`font-bold text-red-400`}>{ind.quantity_short}</div>
                          {ind.quantity_received > 0 && (
                            <div className={`text-[9px] ${ind.quantity_received >= ind.quantity_short ? 'text-green-400' : isDark ? 'text-white/40' : 'text-gray-500'}`}>recv: {ind.quantity_received}/{ind.quantity_short}</div>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          {isReceived ? (
                            <div>
                              <div className={isDark ? 'text-white/60' : 'text-gray-600'}>{ind.tally_po_no || '-'}</div>
                              {ind.tally_po_date && <div className={`text-[9px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{ind.tally_po_date}</div>}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <Input className={`h-7 text-xs w-28 ${inputCls}`} placeholder="PO #" value={edits.tally_po_no ?? ind.tally_po_no ?? ''} onChange={e => editField(ind.id, 'tally_po_no', e.target.value)} />
                              <Input type="date" className={`h-7 text-xs w-32 ${inputCls}`} value={edits.tally_po_date ?? ind.tally_po_date ?? ''} onChange={e => editField(ind.id, 'tally_po_date', e.target.value)} />
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-2 relative">
                          {isReceived ? <span className={isDark ? 'text-white/60' : 'text-gray-600'}>{ind.supplier_name || '-'}</span> : (
                            <>
                              <Input className={`h-7 text-xs w-48 ${inputCls}`} placeholder="Supplier name" value={edits.supplier_name ?? ind.supplier_name ?? ''}
                                onChange={e => { editField(ind.id, 'supplier_name', e.target.value); fetchSupplierSuggest(e.target.value); setOpenSupplierFor(ind.id); }}
                                onFocus={(e) => { fetchSupplierSuggest(e.target.value); setOpenSupplierFor(ind.id); }}
                                onBlur={() => setTimeout(() => setOpenSupplierFor(null), 200)}
                                autoComplete="off"
                                data-testid={`supplier-input-${ind.id}`}
                              />
                              {openSupplierFor === ind.id && supplierSuggest.length > 0 && (
                                <div className={`absolute z-30 left-2 top-full mt-1 w-56 max-h-40 overflow-y-auto rounded-lg shadow-lg border text-xs ${isDark ? 'bg-[#0c1222] border-white/10' : 'bg-white border-gray-200'}`}>
                                  {supplierSuggest.slice(0, 10).map((s, k) => (
                                    <button key={k} type="button"
                                      onMouseDown={() => { editField(ind.id, 'supplier_name', s.supplier_name); setOpenSupplierFor(null); }}
                                      className={`w-full text-left px-3 py-1.5 hover:bg-blue-500/10 ${isDark ? 'text-white border-b border-white/5' : 'text-gray-900 border-b border-gray-50'}`}
                                      data-testid={`supplier-suggest-${ind.id}-${k}`}
                                    >
                                      <span className="font-medium">{s.supplier_name}</span>
                                      <span className={`ml-2 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{s.use_count}× used</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {isReceived ? <span className={isDark ? 'text-white/60' : 'text-gray-600'}>{ind.supplier_eta || '-'}</span> : (
                            <Input type="date" className={`h-7 text-xs w-32 ${inputCls}`} value={edits.supplier_eta ?? ind.supplier_eta ?? ''} onChange={e => editField(ind.id, 'supplier_eta', e.target.value)} data-testid={`eta-input-${ind.id}`} />
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {isReceived ? (
                            <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS['Received']}`} data-testid={`status-badge-${ind.id}`}>Indent Completed</span>
                          ) : (
                            <select className={`h-7 rounded text-[10px] px-1.5 border ${selectCls}`}
                              value={edits.procurement_status ?? ind.procurement_status}
                              onChange={e => editField(ind.id, 'procurement_status', e.target.value)}
                              data-testid={`status-select-${ind.id}`}>
                              <option value="Awaiting PO">Indent Initiated</option>
                              <option value="PO Placed">PO Placed</option>
                              <option value="In Transit">In Transit</option>
                            </select>
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            {ind.po_proof_url && (
                              <a href={fileUrl(ind.po_proof_url)} target="_blank" rel="noreferrer" className={`text-[10px] inline-flex items-center gap-0.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} title={`Customer PO ${ind.customer_po_number || ''}`}>
                                <FileText size={11} /> Cust PO
                              </a>
                            )}
                            {ind.drawing_url ? (
                              <a href={fileUrl(ind.drawing_url)} target="_blank" rel="noreferrer" className={`text-[10px] inline-flex items-center gap-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} title="Drawing"><Download size={11} /> Drawing</a>
                            ) : !isReceived ? (
                              <label className={`cursor-pointer text-[10px] inline-flex items-center gap-0.5 ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-700'}`} title="Upload drawing — will also appear on SO">
                                <Upload size={11} /> Drawing
                                <input type="file" accept="image/*,.pdf,.dwg" className="hidden" onChange={(e) => handleIndentDrawingUpload(ind.id, e)} />
                              </label>
                            ) : null}
                            {ind.purchase_drawing_url && ind.purchase_drawing_url !== ind.drawing_url && (
                              <a href={fileUrl(ind.purchase_drawing_url)} target="_blank" rel="noreferrer" className={`text-[10px] inline-flex items-center gap-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} title="Purchase override drawing"><Download size={11} /> PUR</a>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            {(() => {
                              const issues = indentValidationIssues(ind);
                              return issues.length > 0 && (
                                <div className={`text-amber-500`} title={`Missing: ${issues.join(', ')}`}>
                                  <AlertCircle size={14} />
                                </div>
                              );
                            })()}
                            <Button size="sm" variant="ghost" className={`h-6 w-6 p-0 ${isDark ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`}
                              onClick={() => openComms(ind)} title="Supplier communications" data-testid={`comms-${ind.id}`}>
                              <MessageSquare size={13} />
                              {ind.last_communication_at && <span className="absolute -mt-2 -mr-2 w-1.5 h-1.5 rounded-full bg-blue-500" />}
                            </Button>
                            {!isReceived && ind.procurement_status === 'Awaiting PO' && ind.quantity_short > 1 && (
                              <Button size="sm" variant="ghost" className={`h-6 w-6 p-0 ${isDark ? 'text-purple-300 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                                onClick={() => openSplit(ind)} title="Split indent for trial / partial procurement" data-testid={`split-${ind.id}`}>
                                <Split size={13} />
                              </Button>
                            )}
                            {!isReceived && (
                              <>
                                {editing[ind.id] && (
                                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-6 px-2 text-[10px]" onClick={() => handleUpdateWithConfirm(ind.id, ind)} data-testid={`save-${ind.id}`}>
                                    Save
                                  </Button>
                                )}
                                {(edits.procurement_status === 'In Transit' || ind.procurement_status === 'In Transit' || ind.procurement_status === 'Partially Received') && (
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-6 px-2 text-[10px]" onClick={() => handleReceived(ind.id, ind)} data-testid={`mark-received-${ind.id}`}>
                                    <PackageCheck size={12} className="mr-1" /> Received
                                  </Button>
                                )}
                                {/* Short-close: available once supplier engagement has begun (PO placed or later) */}
                                {['PO Placed', 'In Transit', 'Partially Received'].includes(ind.procurement_status) && (
                                  <Button size="sm" variant="outline"
                                    className={`h-6 px-2 text-[10px] border-red-500/40 ${isDark ? 'text-red-300 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'}`}
                                    onClick={() => handleShortClose(ind.id, ind)} title="Cancel remaining unreceived qty"
                                    data-testid={`short-close-${ind.id}`}>
                                    Short-close
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supplier Communications Dialog */}
      <Dialog open={!!commIndent} onOpenChange={v => { if (!v) setCommIndent(null); }}>
        <DialogContent className={`max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {commIndent && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>
                  Supplier Communications — {commIndent.sku_or_item_name}
                </DialogTitle>
              </DialogHeader>
              <div className={`text-xs p-2 rounded mb-3 ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-600'}`}>
                SO: <b>{commIndent.so_number}</b> | Supplier: <b>{commIndent.supplier_name || '—'}</b> | ETA: <b>{commIndent.supplier_eta || '—'}</b>
              </div>

              <form onSubmit={submitComm} className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <select className={`h-9 rounded text-xs px-2 border ${selectCls}`} value={commForm.channel} onChange={e => setCommForm(f => ({ ...f, channel: e.target.value }))} data-testid="comm-channel">
                    <option value="Call">Call</option>
                    <option value="Email">Email</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Note">Note</option>
                  </select>
                  <Input className={`flex-1 ${inputCls}`} placeholder="Note (e.g., 'Spoke to supplier — confirmed dispatch Friday')" value={commForm.note} onChange={e => setCommForm(f => ({ ...f, note: e.target.value }))} required data-testid="comm-note" />
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="comm-submit">Log</Button>
                </div>
              </form>

              <div className="space-y-2">
                {commLog.length === 0 ? (
                  <p className={`text-xs text-center py-6 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>No communications logged yet</p>
                ) : (
                  commLog.map(c => (
                    <div key={c.id} className={`p-3 rounded-lg border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {c.channel === 'Call' && <Phone size={11} className="text-green-400" />}
                          {c.channel === 'Email' && <Mail size={11} className="text-blue-400" />}
                          {c.channel === 'WhatsApp' && <MessageSquare size={11} className="text-emerald-400" />}
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{c.channel}</span>
                          <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>by {c.by_user_name}</span>
                        </div>
                        <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <p className={`text-xs ${isDark ? 'text-white/80' : 'text-gray-800'}`}>{c.note}</p>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Split Indent Dialog (Feature #2) */}
      <Dialog open={!!splitIndent} onOpenChange={v => { if (!v) setSplitIndent(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="split-dialog">
          {splitIndent && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Split Indent — Trial Procurement</DialogTitle>
                <DialogDescription className={isDark ? 'text-white/50' : 'text-gray-500'}>
                  Break this indent into two so you can place a smaller PO first (e.g. for customer trial approval) and procure the rest later.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitSplit} className="space-y-3">
                <div className={`text-xs p-2.5 rounded ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-50 text-gray-700'}`}>
                  <div>Item: <b>{splitIndent.sku_or_item_name}</b></div>
                  <div>SO: <b>{splitIndent.so_number}</b> · Customer: <b>{splitIndent.customer_name || '-'}</b></div>
                  <div className="mt-1">Current indent qty: <b className="text-red-400">{splitIndent.quantity_short}</b></div>
                </div>
                <div>
                  <Label className={isDark ? 'text-white/70' : ''}>Qty to split off (for first/trial PO) *</Label>
                  <Input type="number" min="1" max={splitIndent.quantity_short - 1}
                    className={`mt-1 ${inputCls}`}
                    value={splitForm.split_qty}
                    onChange={e => setSplitForm(f => ({ ...f, split_qty: e.target.value }))}
                    required
                    data-testid="split-qty"
                  />
                  <div className={`text-[10px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    Must be 1 – {splitIndent.quantity_short - 1}. After split: new indent = {splitForm.split_qty || 0}, original = {splitIndent.quantity_short - (parseInt(splitForm.split_qty) || 0)}.
                  </div>
                </div>
                <div>
                  <Label className={isDark ? 'text-white/70' : ''}>Reason</Label>
                  <Input className={`mt-1 ${inputCls}`}
                    placeholder="e.g. Customer trial approval before bulk"
                    value={splitForm.reason}
                    onChange={e => setSplitForm(f => ({ ...f, reason: e.target.value }))}
                    data-testid="split-reason"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setSplitIndent(null)} className={isDark ? 'text-white/60' : 'text-gray-600'}>Cancel</Button>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="split-submit">
                    <Split size={14} className="mr-2" /> Split Indent
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlowPurchase;
